import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Swal from 'sweetalert2';

/**
 * Genera un PDF con el detalle completo del pedido
 * @param {Object} order - Objeto con los datos del pedido
 * @returns {Promise<boolean>} - Retorna true si se generó, false si se canceló
 */
export const generateOrderPDF = async (order) => {
  // Mostrar confirmación antes de generar
  const result = await Swal.fire({
    title: '¿Generar PDF del pedido?',
    html: `
      <p>Se generará un PDF con el detalle completo del pedido <strong>#${order.order_id}</strong></p>
      <p class="text-muted mb-0">Cliente: <strong>${order.client_name || 'Cliente directo'}</strong></p>
    `,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#6c757d',
    confirmButtonText: '<i class="fas fa-download me-2"></i>Descargar PDF',
    cancelButtonText: '<i class="fas fa-times me-2"></i>Cancelar',
    customClass: {
      confirmButton: 'btn btn-primary',
      cancelButton: 'btn btn-secondary'
    }
  });

  // Si canceló, no hacer nada
  if (!result.isConfirmed) {
    return false;
  }

  // Mostrar loading mientras se genera
  Swal.fire({
    title: 'Generando PDF...',
    html: 'Por favor espera un momento',
    allowOutsideClick: false,
    didOpen: () => {
      Swal.showLoading();
    }
  });

  try {
    const doc = new jsPDF();
  
  // Configuración de colores
  const primaryColor = [41, 128, 185]; // Azul
  const successColor = [39, 174, 96]; // Verde
  const textColor = [52, 73, 94]; // Gris oscuro
  
  let yPosition = 20;
  
  // ====================
  // ENCABEZADO
  // ====================
  doc.setFontSize(20);
  doc.setTextColor(...primaryColor);
  doc.text('DETALLE DEL PEDIDO', 105, yPosition, { align: 'center' });
  
  yPosition += 10;
  doc.setFontSize(16);
  doc.text(`Pedido #${order.order_id}`, 105, yPosition, { align: 'center' });
  
  yPosition += 15;
  
  // ====================
  // INFORMACIÓN DEL CLIENTE
  // ====================
  doc.setFontSize(12);
  doc.setTextColor(...textColor);
  doc.setFont(undefined, 'bold');
  doc.text('INFORMACIÓN DEL CLIENTE', 14, yPosition);
  
  yPosition += 8;
  doc.setFont(undefined, 'normal');
  doc.setFontSize(10);
  
  const clientInfo = [
    ['Cliente:', order.client_name || 'Cliente directo'],
    ['DNI:', order.client_dni || 'N/A'],
    ['Teléfono:', order.client_cellphone || 'No disponible'],
    ['Email:', order.customer_email || 'No disponible'],
    ['Dirección:', order.client_address || 'No disponible'],
    ['Ciudad:', order.customer_city || 'No disponible'],
    ['Provincia:', order.customer_province || 'No disponible'],
  ];
  
  clientInfo.forEach(([label, value]) => {
    doc.setFont(undefined, 'bold');
    doc.text(label, 14, yPosition);
    doc.setFont(undefined, 'normal');
    doc.text(value, 50, yPosition);
    yPosition += 6;
  });
  
  yPosition += 5;
  
  // ====================
  // INFORMACIÓN DEL PEDIDO
  // ====================
  doc.setFont(undefined, 'bold');
  doc.setFontSize(12);
  doc.text('INFORMACIÓN DEL PEDIDO', 14, yPosition);
  
  yPosition += 8;
  doc.setFont(undefined, 'normal');
  doc.setFontSize(10);
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-AR');
  };
  
  const orderInfo = [
    ['Estado:', order.status_description || 'Pendiente'],
    ['Fecha de compra:', formatDate(order.inserted_date)],
    ['Vendedor:', order.seller_name || 'N/A'],
    ['Tipo de entrega:', order.delivery_description || 'No especificado'],
    ['Medio de pago:', order.payment_description || 'No especificado'],
  ];
  
  orderInfo.forEach(([label, value]) => {
    doc.setFont(undefined, 'bold');
    doc.text(label, 14, yPosition);
    doc.setFont(undefined, 'normal');
    doc.text(value, 60, yPosition);
    yPosition += 6;
  });
  
  yPosition += 5;
  
  // ====================
  // PRODUCTOS
  // ====================
  doc.setFont(undefined, 'bold');
  doc.setFontSize(12);
  doc.text('PRODUCTOS', 14, yPosition);
  
  yPosition += 5;
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };
  
  // Preparar datos de la tabla (SIN PRECIOS - solo para taller)
  const tableData = [];
  
  if (order.items && order.items.length > 0) {
    order.items.forEach((item) => {
      const productName = item.sku_description || `Producto ${item.sku || 'N/A'}`;
      const material = item.details?.material_name || '-';
      const measurements = item.details?.measurements || '-';
      const quantity = item.quantity || 0;
      
      tableData.push([
        productName,
        material,
        measurements,
        quantity
      ]);
      
      // Si hay colores, agregar una fila extra
      if (item.details?.colors && item.details.colors.length > 0) {
        const colors = item.details.colors.map(c => c.color_name).join(', ');
        tableData.push([
          { content: `Colores: ${colors}`, colSpan: 4, styles: { fontStyle: 'italic', textColor: [100, 100, 100] } }
        ]);
      }
      
      // Si hay accesorios, agregar una fila extra
      if (item.details?.additional_accessories && item.details.additional_accessories.length > 0) {
        const accessories = item.details.additional_accessories.map(a => a.accessory_name).join(', ');
        tableData.push([
          { content: `Accesorios: ${accessories}`, colSpan: 4, styles: { fontStyle: 'italic', textColor: [100, 100, 100] } }
        ]);
      }
      
      // Si hay productos de stock local, agregar filas extra
      if (item.stock_products && item.stock_products.length > 0) {
        item.stock_products.forEach(sp => {
          tableData.push([
            { content: `  ◦ ${sp.product_name} [Stock Local]`, styles: { fontStyle: 'italic', textColor: [39, 174, 96] } },
            { content: '-', styles: { textColor: [100, 100, 100] } },
            { content: '-', styles: { textColor: [100, 100, 100] } },
            { content: sp.quantity || 0, styles: { textColor: [39, 174, 96], halign: 'center' } }
          ]);
        });
      }
    });
  }
  
  autoTable(doc, {
    startY: yPosition,
    head: [['Producto', 'Material', 'Medidas', 'Cant.']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9
    },
    bodyStyles: {
      fontSize: 9,
      textColor: textColor
    },
    columnStyles: {
      3: { halign: 'center' }
    },
    margin: { left: 14, right: 14 }
  });
  
  // Obtener la posición Y final de la tabla
  yPosition = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : yPosition + 60;
  
  // ====================
  // OBSERVACIONES
  // ====================
  if (order.observations) {
    yPosition += 5;
    doc.setFontSize(12);
    doc.setTextColor(...textColor);
    doc.setFont(undefined, 'bold');
    doc.text('OBSERVACIONES', 14, yPosition);
    
    yPosition += 7;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    
    // Split text si es muy largo
    const splitObservations = doc.splitTextToSize(order.observations, 180);
    doc.text(splitObservations, 14, yPosition);
  }
  
  // ====================
  // PIE DE PÁGINA
  // ====================
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Página ${i} de ${pageCount} - Generado el ${new Date().toLocaleDateString('es-AR')} a las ${new Date().toLocaleTimeString('es-AR')}`,
      105,
      285,
      { align: 'center' }
    );
  }
  
  // Guardar el PDF
  doc.save(`Pedido_${order.order_id}_${new Date().toISOString().split('T')[0]}.pdf`);
  
  // Cerrar el loading y mostrar éxito
  Swal.fire({
    title: '¡PDF generado!',
    text: 'El archivo se ha descargado correctamente',
    icon: 'success',
    timer: 2000,
    showConfirmButton: false
  });

  return true;

  } catch (error) {
    console.error('Error generando PDF:', error);
    Swal.fire({
      title: 'Error',
      text: 'Hubo un problema al generar el PDF. Por favor intenta nuevamente.',
      icon: 'error',
      confirmButtonText: 'Entendido'
    });
    return false;
  }
};
